"use client";

import type { Locale } from "@i18n/react";

import { URL_SEARCH } from "../../../lib/constants/urls/urlSearch";
import type { SearchResultSchema } from "../../../lib/types/schema/search/searchResultSchema";
import { searchResultSchema } from "../../../lib/types/schema/search/searchResultSchema";
import type { UseRequestInfiniteQueryProps } from "../../../request/hooks/useRequestInfiniteQuery";
import { useRequestInfiniteQuery } from "../../../request/hooks/useRequestInfiniteQuery";
import { ELASTIC_SEARCH_DIRECTORY } from "./constants/elasticSearchDirectory.constants";
import { PAGE_SIZE_SEARCH } from "./constants/pageSizeSearch.constants";
import { keySearch } from "./keySearch";
import type { SearchItemType } from "./types/enums/searchItemTypeEnum.enums";

export type UseRequestSearchProps = Partial<UseRequestInfiniteQueryProps> & {
  filters: {
    locale: Locale;
    type?: SearchItemType;
  };
  search: string;
};

export const useRequestSearch = ({
  filters,
  search,
}: UseRequestSearchProps) => ({
  infiniteQuery: useRequestInfiniteQuery({
    enabledWithoutSessionToken: true,
    getNextPageParam: (lastPage: SearchResultSchema) =>
      lastPage.meta.page.current < lastPage.meta.page.total_pages
        ? lastPage.meta.page.current + 1
        : null,
    initialPageParam: 1,
    queryFnRequestProps: ({ pageParam }) => ({
      body: JSON.stringify({
        filters: {
          all: [
            {
              languages: [filters.locale],
            },
            {
              type: filters.type
                ? [filters.type]
                : [
                    "page",
                    "collection",
                    "prayer",
                    "radio_station",
                    "setting",
                    "navigation",
                  ],
            },
          ],
        },
        page: {
          current: pageParam,
          size: PAGE_SIZE_SEARCH,
        },
        query: search,
      }),
      method: "POST",
      requestType: "search",
      schema: searchResultSchema,
      url: `${URL_SEARCH}/${ELASTIC_SEARCH_DIRECTORY}/search`,
    }),
    queryKey: keySearch({ filters, search }),
  }),
});
